"use client"

import * as React from 'react'
import { useRouter } from 'next/navigation'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { ErrorMessage } from '@open-mercato/ui/backend/detail'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { deleteCrud, updateCrud } from '@open-mercato/ui/backend/utils/crud'
import { readApiResultOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import {
  LocationCrudForm,
  buildLocationPayload,
  toLocationFormValues,
  type LocationFormValues,
} from '../../../components/LocationCrudForm'
import { normalizeLocationRecord, type LocationRecord } from '../../../lib/locationPresentation'

export default function LocationDetailPage({ params }: { params?: { id?: string } }) {
  const locationId = params?.id ?? ''
  const t = useT()
  const router = useRouter()
  const [initialValues, setInitialValues] = React.useState<LocationFormValues | null>(null)
  const [isLoading, setIsLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!locationId) return
    let cancelled = false

    async function loadLocation() {
      setIsLoading(true)
      setError(null)
      try {
        const payload = await readApiResultOrThrow<Record<string, unknown>>(
          `/api/freight/locations/${encodeURIComponent(locationId)}`,
          undefined,
          { errorMessage: t('locations.errors.loadDetail', 'Failed to load location.') },
        )
        if (cancelled) return
        const record: LocationRecord = normalizeLocationRecord(payload)
        setInitialValues(toLocationFormValues(record))
      } catch (loadError) {
        console.error('locations.detail.load', loadError)
        if (!cancelled) {
          setError(t('locations.errors.loadDetail', 'Failed to load location.'))
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    void loadLocation()
    return () => {
      cancelled = true
    }
  }, [locationId, t])

  const handleSubmit = React.useCallback(async (values: LocationFormValues) => {
    if (!locationId) return
    const payload = buildLocationPayload(values, t, { id: locationId })
    await updateCrud('freight/locations', payload, {
      errorMessage: t('locations.errors.update', 'Failed to update location.'),
    })
    flash(t('locations.messages.updated', 'Location updated.'), 'success')
    router.push('/backend/locations')
  }, [locationId, router, t])

  const handleDelete = React.useCallback(async () => {
    if (!locationId) return
    await deleteCrud('freight/locations', locationId, {
      errorMessage: t('locations.errors.delete', 'Failed to delete location.'),
    })
    flash(t('locations.messages.deleted', 'Location deleted.'), 'success')
    router.push('/backend/locations')
  }, [locationId, router, t])

  return (
    <Page>
      <PageBody>
        {error ? <ErrorMessage label={error} /> : null}
        <LocationCrudForm
          title={t('locations.form.editTitle', 'Edit location')}
          submitLabel={t('locations.form.actions.save', 'Save')}
          backHref="/backend/locations"
          cancelHref="/backend/locations"
          initialValues={initialValues ?? {
            id: locationId,
            name: '',
            code: '',
            type: '',
            unlocode: '',
            addressStreet: '',
            addressCity: '',
            addressPostalCode: '',
            addressCountry: '',
            addressRegion: '',
            supportedModes: [],
            capabilities: [],
            gateInstructions: '',
            operatingHoursJson: '',
            slotBookingEnabled: false,
            isActive: true,
          }}
          onSubmit={handleSubmit}
          onDelete={handleDelete}
          isLoading={isLoading}
          loadingMessage={t('locations.form.loading', 'Loading location...')}
        />
      </PageBody>
    </Page>
  )
}
